import * as React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { Container, Col, Row, Button } from 'reactstrap'

import { UNREACHABLE_ERROR_STATUS_CODE } from 'api/utils'

import { login, selectSessionStatus, clearErrorMessage } from 'slices/sessionSlice'
import { selectTenantsStatus } from 'slices/tenantsSlice'

import { InputFormat, SinglePageCard } from 'components/common'
import * as Rules from 'components/common/FormFormat/ValidationRules'
import { ColumnSizes } from 'components/common/utils'

import useAuthority from 'hooks/useAuthority'

import AutoLogin from './AutoLogin'

import styles from './styles.module.scss'

const Login: React.FC = () => {
  const [modalErrorMessage, setModalErrorMessage] = React.useState<string | undefined>(undefined)
  const [email, setEmail] = React.useState<string | undefined>(undefined)
  const [password, setPassword] = React.useState<string | undefined>(undefined)
  const [emailValidity, setEmailValidity] = React.useState(false)
  const [passwordValidity, setPasswordValidity] = React.useState(false)

  const dispatch = useDispatch()
  const { loggedIn, activated, errorMessage } = useSelector(selectSessionStatus, shallowEqual)
  const { tenant } = useSelector(selectTenantsStatus, shallowEqual)

  const { isTenantAdmin, isAdmin, isProcessAdmin } = useAuthority()
  const navigate = useNavigate()
  const onSubmit = (e: React.MouseEvent) => {
    e.preventDefault()
    if (email && password) {
      dispatch(login({ email, password }))
    }
  }

  React.useEffect(() => {
    if (!loggedIn) {
      return
    }

    // メールで渡すサインアップURLはPC版であるため、タブレット版ではアクティベートしない
    if (!activated && process.env.REACT_APP_TEAM_MODE !== 'true') {
      navigate('/activate')
      return
    }

    // タブレット版から全体管理者画面にはログインできない
    if (isTenantAdmin) {
      process.env.REACT_APP_TEAM_MODE === 'true'
        ? setModalErrorMessage('メールアドレスかパスワードが間違っています。')
        : navigate('/tenants')
      return
    }

    if (!tenant || !(isAdmin || isProcessAdmin)) {
      return
    }

    if (!tenant.isActive) {
      navigate('/suspension')
      return
    }

    if (process.env.REACT_APP_TEAM_MODE === 'true') {
      // グループ管理画面向け遷移
      navigate('/team-select-workspaces')
      return
    }
    navigate('/dashboard')
  }, [navigate, loggedIn, activated, tenant, isTenantAdmin, isProcessAdmin, isAdmin])

  React.useEffect(() => {
    // UNREACHABLE_ERROR_STATUS_CODEのときにはエラーダイアログが出るのでNotificationは出さない
    if (errorMessage === UNREACHABLE_ERROR_STATUS_CODE || errorMessage === '') {
      setModalErrorMessage(undefined)
    } else {
      // ログイン不要ページでUNAUTHORIZED_ERROR_STATUS_CODEのときはNotificationを出す
      setModalErrorMessage('メールアドレスかパスワードが間違っています。')
    }
  }, [errorMessage])

  const disabled = React.useMemo(
    () => !(email && emailValidity && password && passwordValidity),
    [email, emailValidity, password, passwordValidity]
  )

  return (
    <SinglePageCard
      errorMessage={modalErrorMessage}
      onHideNotification={() => {
        setModalErrorMessage(undefined)
        dispatch(clearErrorMessage())
      }}
    >
      <Container>
        <InputFormat
          label="メールアドレス"
          type="email"
          placeholder="メールアドレス"
          value={email}
          size={ColumnSizes.large}
          maxLength={100}
          onChange={value => setEmail(value)}
          validations={[Rules.Required]}
          onValidate={setEmailValidity}
          className="mb-3"
        />

        <InputFormat
          label="パスワード"
          type="password"
          placeholder="パスワード"
          value={password}
          size={ColumnSizes.large}
          maxLength={100}
          onChange={value => setPassword(value)}
          validations={[Rules.Required]}
          onValidate={setPasswordValidity}
        />

        <Row>
          <Col className="text-center mt-3">
            <Link className="text-decoration-none" to="/password-reset">
              パスワードを忘れた方
            </Link>
          </Col>
        </Row>
        <Row>
          <Col className="text-center mt-3">
            <Button disabled={disabled} color="primary" className={styles.button} onClick={onSubmit}>
              ログイン
            </Button>
          </Col>
        </Row>
      </Container>
      {process.env.NODE_ENV === 'development' && <AutoLogin setEmail={setEmail} setPassword={setPassword} />}
    </SinglePageCard>
  )
}

export default Login
